/**
 * Catalog feature — UI-only presentation settings.
 *
 * Per the three-bucket config convention (ADR 0002), keep visual settings
 * here, business rules on the backend (`rules.py`), and infra under
 * `backend/.../config/backend.py`.
 */

export const CATALOG_UI = {
  /** Days-until-expiry threshold for the red "urgent" expiry badge. */
  EXPIRY_URGENT_DAYS: 7,
  /** Days-until-expiry threshold for the amber "soon" expiry badge. */
  EXPIRY_WARNING_DAYS: 30,
  /** ISO 4217 currency code used for package price display. */
  PRICE_CURRENCY: 'INR',
  /** Smallest currency unit per major (paise/rupee). */
  PRICE_MINOR_UNITS_PER_MAJOR: 100,
} as const;

export function formatPaise(paise: number): string {
  const rupees = paise / CATALOG_UI.PRICE_MINOR_UNITS_PER_MAJOR;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: CATALOG_UI.PRICE_CURRENCY,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rupees);
}

/**
 * Convert integer paise → a rupee string suitable for a form <input value>.
 *
 * Whole-rupee amounts render without decimals (`2900000` → `"29000"`); amounts
 * with paise keep up to 2 decimals (`123450` → `"1234.50"`). No thousands
 * separators (an <input type="number"> can't contain commas).
 */
export function paiseToRupeeInput(paise: number): string {
  if (!Number.isFinite(paise)) return '';
  const rupees = paise / CATALOG_UI.PRICE_MINOR_UNITS_PER_MAJOR;
  return Number.isInteger(rupees) ? String(rupees) : rupees.toFixed(2);
}

/**
 * Convert a user-entered rupee string (e.g. `"29000.00"`, `"29,000"`, `"₹1,234.50"`)
 * → integer paise for the API. Empty / invalid input → 0.
 */
export function rupeeInputToPaise(input: string): number {
  const cleaned = String(input).replace(/[,\s₹]/g, '').trim();
  if (cleaned === '') return 0;
  const rupees = Number(cleaned);
  if (!Number.isFinite(rupees)) return 0;
  return Math.round(rupees * CATALOG_UI.PRICE_MINOR_UNITS_PER_MAJOR);
}

